import React, { useCallback, useEffect, useState } from 'react';
import { sitesV2Api, SiteV2 } from '../../../api/sitesV2Api';
import { getAvatarUrl } from '../../../utils/avatarUtils';
import { siteMembersApi, SiteMember } from '../../../api/siteMembersApi';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { UniversalHeader } from '../../../shared/ui';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import ObjectWallTab from './ObjectWallTab';
import styles from './SiteProfilePage.module.css';

interface SiteProfilePageProps {
  siteId: number;
  onBack: () => void;
  onOpenWorkspace?: (siteId: number) => void; // переход на полную страницу объекта (только для участников)
  onUserClick?: (userId: number) => void;
}

const SiteProfilePage: React.FC<SiteProfilePageProps> = ({
  siteId,
  onBack,
  onOpenWorkspace,
  onUserClick,
}) => {
  const { user } = useAuth();
  const { pushBackLayer, popBackLayer } = useNavigation();
  const [site, setSite] = useState<SiteV2 | null>(null);
  const [members, setMembers] = useState<SiteMember[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'feed' | 'team'>('feed');

  const isMember = members.some(m => m.userId === user?.id && m.isActive);

  useEffect(() => {
    pushBackLayer(onBack);
    return () => { popBackLayer(); };
  }, [onBack, pushBackLayer, popBackLayer]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [siteData, membersData] = await Promise.all([
        sitesV2Api.getSite(siteId),
        siteMembersApi.getActiveSiteMembers(siteId),
      ]);
      setSite(siteData);
      setMembers(membersData);

      // Аватарка объекта
      try {
        const avatarMedia = await sitesV2Api.getAvatar(siteId);
        if (avatarMedia) {
          setAvatarUrl(getAvatarUrl(avatarMedia, 'small') || getAvatarUrl(avatarMedia, 'medium'));
        }
      } catch {
        // аватарка необязательна
      }
    } catch (err) {
      console.error('Error loading site profile:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const getInitials = (name: string) =>
    name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const getAvatarColor = (id: number) => {
    const colors = ['#6c5ce7','#00b894','#e17055','#0984e3','#fd79a8','#a29bfe','#55efc4','#74b9ff'];
    return colors[id % colors.length];
  };

  const getPrimaryRole = (member: SiteMember): string => {
    if (member.roles && member.roles.length > 0) {
      return member.roles[0].roleName || 'Участник';
    }
    return member.roleName || 'Участник';
  };

  return (
    <div className={styles.page}>
      {/* Фиксированная хромовая часть: шапка + кнопка участника + табы */}
      <div className={styles.chrome}>
        <UniversalHeader
          className="object-page-header"
          suppressBackRegistration={true}
          title={loading ? 'Загрузка...' : (site?.name || 'Объект')}
          onBack={onBack}
          avatar={site ? {
            icon: site.closed ? 'ri-archive-line' : 'ri-building-2-line',
            imageUrl: avatarUrl || undefined,
            color: site.closed ? 'var(--theme-text-muted)' : 'var(--object-page-avatar-bg, #ffffff)'
          } : undefined}
          infoRow={site ? {
            members: {
              count: members.length,
              getLabel: (n: number) => n === 1 ? 'участник' : n < 5 ? 'участника' : 'участников'
            },
            ...(site.closed ? { status: { text: 'Завершён', type: 'closed' as const } } : {})
          } : undefined}
        />

        {isMember && onOpenWorkspace && (
          <button
            type="button"
            className={styles.workspaceButton}
            onClick={() => onOpenWorkspace(siteId)}
          >
            <i className="ri-dashboard-line" aria-hidden />
            Открыть рабочее пространство
          </button>
        )}

        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'feed' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('feed')}
          >
            Лента
          </button>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'team' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('team')}
          >
            Команда
          </button>
          <span
            className={styles.tabIndicator}
            style={{ transform: `translateX(${activeTab === 'team' ? '100%' : '0'})` }}
            aria-hidden
          />
        </div>
      </div>

      {/* Скроллируемый контент */}
      <div className={styles.scroll}>
        {activeTab === 'feed' && (
          <div className={styles.feedContent}>
            <ObjectWallTab siteId={siteId} canPost={isMember} />
          </div>
        )}

        {activeTab === 'team' && (
          <div className={styles.teamList}>
            {members.length === 0 ? (
              <div className={styles.emptyTeam}>
                <i className="ri-group-line" />
                <p>Участники не найдены</p>
              </div>
            ) : (
              members.map(member => (
                <div
                  key={member.id}
                  className={styles.memberRow}
                  onClick={() => onUserClick && member.userId && onUserClick(member.userId)}
                  style={{ cursor: onUserClick ? 'pointer' : 'default' }}
                >
                  <div className={styles.memberAvatar}>
                    {member.avatarUrl ? (
                      <img
                        src={member.avatarUrl}
                        alt={member.userName || ''}
                        className={styles.memberAvatarImg}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div
                        className={styles.memberAvatarFallback}
                        style={{ background: getAvatarColor(member.userId) }}
                      >
                        {getInitials(member.userName || '?')}
                      </div>
                    )}
                  </div>
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>{member.userName || 'Участник'}</span>
                    <span className={styles.memberRole}>{getPrimaryRole(member)}</span>
                  </div>
                  {member.userId === user?.id && (
                    <span className={styles.youBadge}>Вы</span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteProfilePage;
